import CloseIcon from '@mui/icons-material/Close';
import { Checkbox, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import isequal from 'lodash.isequal';
import { useEffect, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { EventActionsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import IconButton from '../Buttons/IconButton';
import styles from './Inputs.module.scss';
import type { SelectInputOption } from './SelectInput';

interface IMuiDropdownProps<T> {
    values: T[];
    options: SelectInputOption<T>[];
    handleOptionSelect: (values: T[]) => void;
    label?: string;
    placeholder?: string;
    // TODO: Finish option to have removable elements.
    labelType?: 'removableElements' | 'selectedTextValues';
    minimumSelectionLimit?: number;
    maximumSelectionLimit?: number;
    limitMessage?: string;
    disabled?: boolean;
    displayValues?: boolean;
    testId?: string;
    track: TrackingEvent;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
const CheckboxDropdown = <T,>(props: IMuiDropdownProps<T>): JSX.Element => {
    const [formValues, setFormValues] = useState<string[]>([]);
    const [error, setError] = useState(false);
    const [currentValues, setCurrentValues] = useState(props.values);

    // Labels are looked up in the options, so they have to be rebuilt whenever either side changes. Watching the values
    // alone loses the selection when the options are still loading, and values without a matching option are dropped.
    useEffect(() => {
        const newFormValues = props.values
            .map((value) => props.options.find((x) => isequal(x.value, value))?.label)
            .filter((label): label is string => !!label);

        if (!isequal(newFormValues, formValues)) {
            setFormValues(newFormValues);
        }
    }, [props.values, props.options]);

    const handleOptionChange = (event: SelectChangeEvent<string[]>) => {
        const newLabels = event.target.value as string[];
        const newValues: T[] = [];
        newLabels.forEach((element: string) => {
            const option = props.options.find((x) => x.label === element)?.value;
            if (option) {
                newValues.push(option);
            }
        });

        if (props.minimumSelectionLimit && newValues.length < props.minimumSelectionLimit) {
            setError(true);
        } else {
            setError(false);
            loggingService.trackEventWithAnalytics(
                EventActionsEnum.ChartMultiselect,
                (event.target as HTMLInputElement).value,
                newValues.length > formValues.length ? 'Add' : 'Remove',
                EventActionsEnum.ChartMultiselect,
            );
            trackingService.track({ ...props.track, eventValue: newLabels });
        }
        props.handleOptionSelect(newValues);
    };

    useEffect(() => {
        setCurrentValues(props.values);
    }, [props.values]);

    return (
        <>
            {props.label && <p className={styles.input_dropdown_label}>{props.label}</p>}
            <FormControl fullWidth sx={{ m: 1, margin: 0 }} size="small" error={error}>
                {props.labelType === 'removableElements' && (
                    <InputLabel>
                        <div className={styles.input_removable_elements}>
                            {currentValues.map((x, index, array) => (
                                <div className={styles.input_removable_element}>
                                    <IconButton
                                        key={`selected${x}`}
                                        handleClick={() => {
                                            const newValues = array.filter((y) => y !== x);
                                            props.handleOptionSelect(newValues);
                                        }}
                                        color="default"
                                        size="small"
                                        track={{
                                            eventCategory: props.track.eventCategory,
                                            eventAction: `${props.track.eventAction}RemoveClicked`,
                                            eventValue: props.options.find((option) => isequal(option.value, x))?.label,
                                        }}
                                    >
                                        <p>{props.options.find((y) => y.value === x)!.label}</p> <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    </InputLabel>
                )}
                {props.labelType === 'selectedTextValues' && (
                    <InputLabel shrink={false}>
                        <p className={styles.input_multiselect_text_values_labels}>
                            {props.values.map((x) => props.options.find((y) => y.value === x)?.label).join(', ')}
                        </p>
                    </InputLabel>
                )}
                {props.placeholder && (
                    <InputLabel shrink={false} sx={{ color: scssVariables.mainTextColor }}>
                        {props.placeholder}
                    </InputLabel>
                )}
                <Select
                    data-testid={props.testId}
                    multiple
                    className={styles.input_auto_complete}
                    value={formValues}
                    onChange={handleOptionChange}
                    renderValue={(selected) => (props.displayValues ? selected.join(', ') : '')}
                    sx={{ backgroundColor: scssVariables.plainWhite, fontFamily: scssVariables.fontFamily }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 500,
                                fontFamily: scssVariables.fontFamily,
                                '& .MuiMenuItem-root': {
                                    fontFamily: scssVariables.fontFamily,
                                },
                                '& .MuiCheckbox-root': {
                                    fontFamily: scssVariables.fontFamily,
                                },
                            },
                        },
                    }}
                    disabled={props.disabled}
                >
                    <MenuItem
                        value=""
                        disabled
                        disableRipple
                        disableTouchRipple
                        sx={{ color: scssVariables.black }}
                        data-testid={`${props.testId}OptionDefault`}
                    >
                        <em>{props.limitMessage}</em>
                    </MenuItem>
                    {props.options.map((option) => (
                        <MenuItem
                            key={`${option.label}`}
                            value={`${option.label}`}
                            disabled={
                                (props.maximumSelectionLimit &&
                                    formValues.length >= props.maximumSelectionLimit &&
                                    !(formValues.indexOf(option.label) > -1)) as boolean
                            }
                            data-testid={`${props.testId}Option${option.label}`}
                        >
                            <Checkbox
                                checked={formValues.indexOf(option.label) > -1}
                                disabled={
                                    (props.maximumSelectionLimit &&
                                        formValues.length >= props.maximumSelectionLimit &&
                                        !(formValues.indexOf(option.label) > -1)) as boolean
                                }
                            />
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );
};

export default CheckboxDropdown;
